import React, { useState, useRef, useEffect } from 'react';
import { X, Send, BookOpen, ExternalLink, FileText, Youtube, ChevronRight, Loader2, HelpCircle } from 'lucide-react';

/**
 * GameHelpChat - Sidebar chat for game-specific rule questions
 * Uses strict prompting to only answer based on official rules
 */
const GameHelpChat = ({ 
    game, 
    onClose,
    isOpen = true
}) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [expansions, setExpansions] = useState([]);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Load owned expansions for the game
    useEffect(() => {
        if (game?.id) {
            fetch(`/api/games/${game.id}/expansions`)
                .then(res => res.json())
                .then(data => {
                    const ownedExpansions = data.filter(e => e.is_owned);
                    setExpansions(ownedExpansions);
                })
                .catch(err => console.error('Failed to load expansions:', err));
        }
    }, [game?.id]);

    // Initialize with welcome message (including expansions info)
    useEffect(() => {
        if (game && messages.length === 0) {
            let welcomeMsg = `Tervetuloa ${game.name} -sääntöavustajaan! 📖\n\nVoin auttaa sinua pelin säännöissä. Vastaan VAIN virallisten sääntöjen mukaan - en arvaa tai tulkitse.`;
            
            if (expansions.length > 0) {
                welcomeMsg += `\n\n📦 Omistamanne lisäosat: ${expansions.map(e => e.name).join(', ')}\nVoin vastata myös näiden lisäosien säännöistä!`;
            }
            
            welcomeMsg += `\n\nKysy esimerkiksi:\n• "Miten vuoro etenee?"\n• "Mitä tapahtuu tasatilanteessa?"\n• "Kuinka pisteet lasketaan?"`;
            
            setMessages([{
                role: 'assistant',
                content: welcomeMsg,
                sources: []
            }]);
        }
    }, [game, expansions]);

    const sendMessage = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setIsLoading(true);

        try {
            const response = await fetch('/api/oracle/game-help', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gameName: game?.name,
                    gameId: game?.id,
                    bggId: game?.bgg_id,
                    manualUrl: game?.manual_url,
                    rulesUrl: game?.rules_url,
                    question: userMessage,
                    expansions: expansions.map(e => ({ name: e.name, bggId: e.bgg_id }))
                })
            });

            const data = await response.json();

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: data.answer || data.text || 'Pahoittelut, en pystynyt vastaamaan kysymykseen.',
                sources: data.sources || [],
                confidence: data.confidence
            }]);
        } catch (error) {
            console.error('Game help error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Yhteysvirhe. Kokeile uudelleen hetken kuluttua.',
                sources: [],
                isError: true
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Quick question suggestions
    const quickQuestions = [
        'Miten peli valmistellaan?',
        'Miten vuoro etenee?',
        'Miten peli päättyy?',
        'Miten pisteet lasketaan?'
    ];

    const askQuickQuestion = (question) => {
        setInput(question);
        setTimeout(() => sendMessage(), 100);
    };

    if (!isOpen) return null;

    return (
        <div className="game-help-chat">
            {/* Header */}
            <div className="help-header">
                <div className="help-header-title">
                    <BookOpen size={20} />
                    <div>
                        <h3>Sääntöapu</h3>
                        <span className="help-game-name">{game?.name || 'Peli'}</span>
                    </div>
                </div>
                <button className="help-close-btn" onClick={onClose}>
                    <X size={18} />
                </button>
            </div>

            {/* Game Links */}
            {(game?.manual_url || game?.rules_url || game?.video_url || game?.bgg_id) && (
                <div className="help-links">
                    {game?.bgg_id && (
                        <a 
                            href={`https://boardgamegeek.com/boardgame/${game.bgg_id}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="help-link"
                        >
                            <ExternalLink size={14} />
                            BGG
                        </a>
                    )}
                    {game?.manual_url && (
                        <a 
                            href={game.manual_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="help-link"
                        >
                            <FileText size={14} />
                            Säännöt (PDF)
                        </a>
                    )}
                    {game?.video_url && (
                        <a 
                            href={game.video_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="help-link"
                        >
                            <Youtube size={14} />
                            Video
                        </a>
                    )}
                </div>
            )}

            {/* Messages */}
            <div className="help-messages">
                {messages.map((msg, idx) => (
                    <div 
                        key={idx} 
                        className={`help-message ${msg.role} ${msg.isError ? 'error' : ''}`}
                    >
                        {msg.role === 'assistant' && (
                            <div className="message-header">
                                <ChevronRight size={14} />
                                <span>Sääntöavustaja</span>
                                {msg.confidence && (
                                    <span className={`confidence-badge ${msg.confidence}`}>
                                        {msg.confidence === 'high' ? '✓ Varma' : 
                                         msg.confidence === 'medium' ? '~ Todennäköinen' : 
                                         '? Epävarma'}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="message-content">
                            {msg.content.split('\n').map((line, i) => (
                                <span key={i}>
                                    {line}
                                    {i < msg.content.split('\n').length - 1 && <br />}
                                </span>
                            ))}
                        </div>

                        {/* Source Links */}
                        {msg.sources && msg.sources.length > 0 && (
                            <div className="message-sources">
                                <span className="sources-label">📚 Lähteet:</span>
                                {msg.sources.map((src, i) => (
                                    <a 
                                        key={i}
                                        href={src.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="source-link"
                                    >
                                        {src.type === 'pdf' && <FileText size={12} />}
                                        {src.type === 'bgg' && <ExternalLink size={12} />}
                                        {src.type === 'video' && <Youtube size={12} />}
                                        {src.label || src.type}
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                    <div className="help-message assistant loading">
                        <div className="message-header">
                            <ChevronRight size={14} />
                            <span>Sääntöavustaja</span>
                        </div>
                        <div className="message-content">
                            <Loader2 size={16} className="spin" />
                            <span>Tarkistan säännöistä...</span>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions */}
            {messages.length <= 1 && (
                <div className="quick-questions">
                    <span className="quick-label">
                        <HelpCircle size={14} />
                        Pikakyselyt:
                    </span>
                    <div className="quick-buttons">
                        {quickQuestions.map((q, i) => (
                            <button 
                                key={i}
                                className="quick-btn"
                                onClick={() => askQuickQuestion(q)}
                            >
                                {q}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Input Area */}
            <div className="help-input-area">
                <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Kysy säännöistä..."
                    disabled={isLoading}
                    maxLength={300}
                    className="help-input"
                />
                <button 
                    onClick={sendMessage}
                    disabled={isLoading || !input.trim()}
                    className="help-send-btn"
                >
                    <Send size={18} />
                </button>
            </div>

            {/* Disclaimer */}
            <div className="help-disclaimer">
                ⚠️ Vastaukset perustuvat virallisiin sääntöihin. Tarkista epäselvissä tilanteissa alkuperäiset säännöt.
            </div>

            <style>{`
                .game-help-chat {
                    position: fixed;
                    top: 0;
                    right: 0;
                    width: 380px;
                    height: 100vh;
                    background: #161b22;
                    border-left: 1px solid #30363d;
                    display: flex;
                    flex-direction: column;
                    z-index: 9999;
                    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.3);
                }

                .help-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    border-bottom: 1px solid #30363d;
                    background: rgba(0, 0, 0, 0.3);
                }

                .help-header-title {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    color: #fff;
                }

                .help-header-title h3 {
                    margin: 0;
                    font-size: 1rem;
                }

                .help-game-name {
                    display: block;
                    color: #8b949e;
                    font-size: 0.8rem;
                }

                .help-close-btn {
                    background: rgba(255, 255, 255, 0.1);
                    border: none;
                    color: #8b949e;
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }

                .help-close-btn:hover {
                    background: rgba(255, 255, 255, 0.2);
                    color: #fff;
                }

                .help-links {
                    display: flex;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    border-bottom: 1px solid #30363d;
                    flex-wrap: wrap;
                }

                .help-link {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.375rem 0.625rem;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid #30363d;
                    border-radius: 6px;
                    color: #58a6ff;
                    font-size: 0.75rem;
                    text-decoration: none;
                    transition: all 0.2s;
                }

                .help-link:hover {
                    background: rgba(88, 166, 255, 0.1);
                    border-color: #58a6ff;
                }

                .help-messages {
                    flex: 1;
                    overflow-y: auto;
                    padding: 1rem;
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .help-message {
                    padding: 0.75rem;
                    border-radius: 8px;
                }

                .help-message.user {
                    background: #0d419d;
                    color: #fff;
                    align-self: flex-end;
                    max-width: 85%;
                }

                .help-message.assistant {
                    background: rgba(0, 0, 0, 0.3);
                    border: 1px solid #30363d;
                }

                .help-message.error {
                    border-color: #f85149;
                }

                .message-header {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    margin-bottom: 0.5rem;
                    font-size: 0.75rem;
                    color: #8b949e;
                }

                .confidence-badge {
                    margin-left: auto;
                    padding: 0.125rem 0.375rem;
                    border-radius: 4px;
                    font-size: 0.65rem;
                }

                .confidence-badge.high {
                    background: rgba(39, 174, 96, 0.2);
                    color: #27ae60;
                }

                .confidence-badge.medium {
                    background: rgba(241, 196, 15, 0.2);
                    color: #f1c40f;
                }

                .confidence-badge.low {
                    background: rgba(231, 76, 60, 0.2);
                    color: #e74c3c;
                }

                .message-content {
                    color: #c9d1d9;
                    line-height: 1.5;
                    font-size: 0.9rem;
                }

                .help-message.loading .message-content {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #8b949e;
                }

                .spin {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .message-sources {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 0.5rem;
                    margin-top: 0.75rem;
                    padding-top: 0.75rem;
                    border-top: 1px solid #30363d;
                }

                .sources-label {
                    font-size: 0.75rem;
                    color: #8b949e;
                }

                .source-link {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.5rem;
                    background: rgba(88, 166, 255, 0.1);
                    border-radius: 4px;
                    color: #58a6ff;
                    font-size: 0.7rem;
                    text-decoration: none;
                    transition: all 0.2s;
                }

                .source-link:hover {
                    background: rgba(88, 166, 255, 0.2);
                }

                .quick-questions {
                    padding: 0.75rem 1rem;
                    border-top: 1px solid #30363d;
                    background: rgba(0, 0, 0, 0.2);
                }

                .quick-label {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    font-size: 0.75rem;
                    color: #8b949e;
                    margin-bottom: 0.5rem;
                }

                .quick-buttons {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.375rem;
                }

                .quick-btn {
                    padding: 0.375rem 0.625rem;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid #30363d;
                    border-radius: 6px;
                    color: #c9d1d9;
                    font-size: 0.75rem;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .quick-btn:hover {
                    background: rgba(88, 166, 255, 0.1);
                    border-color: #58a6ff;
                    color: #58a6ff;
                }

                .help-input-area {
                    display: flex;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    border-top: 1px solid #30363d;
                    background: rgba(0, 0, 0, 0.3);
                }

                .help-input {
                    flex: 1;
                    padding: 0.625rem 0.875rem;
                    background: #0d1117;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                    outline: none;
                    transition: border-color 0.2s;
                }

                .help-input:focus {
                    border-color: #58a6ff;
                }

                .help-input::placeholder {
                    color: #484f58;
                }

                .help-send-btn {
                    width: 40px;
                    height: 40px;
                    background: #238636;
                    border: none;
                    border-radius: 8px;
                    color: #fff;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }

                .help-send-btn:hover:not(:disabled) {
                    background: #2ea043;
                }

                .help-send-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .help-disclaimer {
                    padding: 0.5rem 1rem;
                    background: rgba(248, 81, 73, 0.1);
                    border-top: 1px solid rgba(248, 81, 73, 0.2);
                    color: #f85149;
                    font-size: 0.7rem;
                    text-align: center;
                }

                /* Mobile Responsive */
                @media (max-width: 768px) {
                    .game-help-chat {
                        width: calc(100% - 24px); /* Leave margin for close button */
                        height: 100vh;
                        height: 100dvh; /* Dynamic viewport height for mobile */
                        display: flex;
                        flex-direction: column;
                    }

                    .help-header {
                        flex-shrink: 0;
                    }

                    .help-links {
                        flex-shrink: 0;
                    }

                    .help-messages {
                        flex: 1;
                        min-height: 0;
                        overflow-y: auto;
                        -webkit-overflow-scrolling: touch;
                    }

                    .quick-questions {
                        flex-shrink: 0;
                    }

                    .help-input-area {
                        flex-shrink: 0;
                        padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
                    }

                    .help-disclaimer {
                        flex-shrink: 0;
                        padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
                    }

                    .help-close-btn {
                        width: 44px;
                        height: 44px;
                    }
                }

                @media (max-width: 480px) {
                    .game-help-chat {
                        width: calc(100% - 20px);
                    }
                }
            `}</style>
        </div>
    );
};

export default GameHelpChat;

